import { memo, useMemo, useEffect } from 'react';
import { Box, Typography, Button, TextField, Divider } from '@mui/material';
import { useOverlayStore } from '../stores/useOverlayStore';
import { useLoadingStore } from '../stores/useLoadingStore';
import { useToast } from '../hooks/toast/useToast';
import { MOTIVES } from '../utils/tripUtils';
import { useOccurrenceModalStore } from '../stores/useOccurrenceModalStore';
import { useSocketStore } from '../stores/useSocketStore';
import { useTripOccurrencesStore } from '../stores/useTripOccurrencesStore';
import CustomSelect from './CustomSelect';
import TripOccurrences from './TripOccurrences';

const EMPTY_OCCURRENCES = [];

const OccurrenceModal = ({ trip, occurrence, onClose }) => {

    const { success, error } = useToast();

    const description = useOccurrenceModalStore(state => state.description);
    const setDescription = useOccurrenceModalStore(state => state.setDescription);

    const motive = useOccurrenceModalStore(state => state.motive);
    const setMotive = useOccurrenceModalStore(state => state.setMotive);

    const setCanClose = useOverlayStore(state => state.setCanClose);

    //CODIGO
    const codigo = trip.CODIGO_INTEGRACAO;

    //NOMELINHA
    const nomeLinha = trip.NOMELINHA;

    const setLoadingBackdrop = useLoadingStore(state => state.setLoadingBackdrop);
    const setLoadingTxt = useLoadingStore(state => state.setLoadingTxt);
    const loadingBackdrop = useLoadingStore(state => state.loadingBackdrop);

    const tripUid = trip?.TRIP_UID;

    const occurrences =
        useTripOccurrencesStore(
            state =>
                state.occurrences[tripUid] ||
                EMPTY_OCCURRENCES
        );

    const motiveItems = useMemo(() => {

        const usedMotiveIds =
            new Set(
                occurrences.map(
                    item => Number(item.motiveId)
                )
            );

        return MOTIVES.map(item => {

            const isUsed =
                item.id !== 0 &&
                usedMotiveIds.has(
                    Number(item.id)
                );

            return {
                ...item,
                disabled: isUsed,
                secondaryText:
                    isUsed
                        ? 'Ocorrência já registrada'
                        : null
            };
        });

    }, [occurrences]);

    const selectedMotiveAlreadyUsed =
        motive !== 0 &&
        occurrences.some(
            item =>
                Number(item.motiveId) ===
                Number(motive)
        );

    const occurrenceTitle = useMemo(() => {
        if (occurrence === 1) {
            return 'Viagem não realizada';
        } else if (occurrence === 2) {
            return 'Viagem atrasada';
        } else if (occurrence === 3) {
            return 'Estouro SLA';
        } else {
            return '';
        }
    }, [occurrence]);

    const handleCloseModal = () => {

        onClose?.();
        useOccurrenceModalStore
            .getState()
            .clear();
    };

    const socket = useSocketStore(state => state.socket);

    const handleConfirm = () => {

        setLoadingBackdrop(true);
        setLoadingTxt('Salvando ocorrência...');
        socket.emit(
            'occurrence:save',
            {
                tripId: trip?.VIAGEM_ID,
                tripUid: trip?.TRIP_UID,
                ocType: occurrence,
                motiveId: motive,
                description,
                occurrenceUid: crypto.randomUUID()
            },
            (response) => {
                setLoadingBackdrop(false);
                setLoadingTxt('');
                if (!response?.ok) {
                    error(response?.message || 'Falha ao salvar ocorrência.');
                    return;
                }

                success('Ocorrência salva com sucesso.');
            }
        );
        
    };

    const handleDeleteOccurrence = (
        occurrence
    ) => {

        if (
            !occurrence?.tripUid ||
            !occurrence?.occurrenceUid
        ) {
            return;
        }

        setLoadingBackdrop(true);
        setLoadingTxt('Excluindo ocorrência...');

        socket.emit(
            'occurrence:delete',
            {
                tripUid: occurrence.tripUid,

                occurrenceUid: occurrence.occurrenceUid
            },
            response => {

                setLoadingBackdrop(false);
                setLoadingTxt('');

                if (!response?.ok) {

                    error(
                        response?.message ||
                        'Falha ao excluir ocorrência.'
                    );

                    return;
                }

                success('Ocorrência excluída com sucesso.');
            }
        );
    };

    useEffect(() => {
        setCanClose(!loadingBackdrop);
    }, [setCanClose, loadingBackdrop])

    return (
        <Box sx={{ minWidth: 680 }}>
            <Typography variant="h6" mb={0}>
                Gerar Ocorrência: {codigo} ({occurrenceTitle})
            </Typography>
            <Typography variant="h6" mb={2}>
                {nomeLinha}
            </Typography>
            <Box sx={{display: 'flex', alignItems: 'center'}}>
                <CustomSelect
                    items={motiveItems}
                    selected={motive}
                    onChange={(val) => setMotive(val)}
                    label="Motivo"
                    allowNone={false}
                    enableSearch={true}
                    scrollToSelected={true}
                />
                <TextField
                    disabled={loadingBackdrop}
                    label="Observação(opcional)"
                    type='text'
                    fullWidth
                    value={description}
                    onChange={(e) => {
                        setDescription(e.target.value);
                    }}
                />
            </Box>
            <TripOccurrences
                occurrences={occurrences}
                onDelete={handleDeleteOccurrence}
            />
            <Divider sx={{ my: 2 }} />
            <Box mt={3} display="flex" justifyContent="space-between">            
                <Button 
                    disabled={loadingBackdrop}
                    variant="contained"
                    color='error'
                    onClick={handleCloseModal}>
                        Fechar
                </Button>

                <Button
                    disabled={
                        loadingBackdrop ||
                        motive === 0 ||
                        selectedMotiveAlreadyUsed
                    }
                    variant="contained"
                    color="success"
                    onClick={handleConfirm}
                >
                    Criar
                </Button>
            </Box>
        </Box>
    );
};

export default memo(OccurrenceModal);